import type { Tag, TagSearchResult } from "@pineseed/gui/types";
import type { ParsedSegment } from "@pineseed/gui/types";

/**
 * Tag Handler - ONLY business logic, NO UI imports, NO direct atom mutations
 * Handles tag search, creation, validation, and mention parsing
 * Pure utility class - state management handled by atoms
 */

export class TagHandler {
  private static instance: TagHandler;

  public static getInstance(): TagHandler {
    if (!TagHandler.instance) {
      TagHandler.instance = new TagHandler();
    }
    return TagHandler.instance;
  }

  /**
   * Search tags with fuzzy matching and scoring
   */
  public searchTags(tags: Tag[], query: string): TagSearchResult[] {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();
    const results: TagSearchResult[] = [];

    tags.forEach((tag) => {
      // Check name match
      const nameScore = this.calculateMatchScore(tag.name, normalizedQuery);
      if (nameScore > 0) {
        results.push({
          tag,
          matchScore: nameScore,
          matchType: "name",
        });
        return; // Skip other checks if name matches
      }

      // Check description match
      if (tag.description) {
        const descScore = this.calculateMatchScore(
          tag.description,
          normalizedQuery,
        );
        if (descScore > 0) {
          results.push({
            tag,
            matchScore: descScore * 0.8, // Lower priority than name
            matchType: "description",
          });
          return;
        }
      }

      // Check type match
      const typeScore = this.calculateMatchScore(tag.type, normalizedQuery);
      if (typeScore > 0) {
        results.push({
          tag,
          matchScore: typeScore * 0.6, // Lower priority than description
          matchType: "type",
        });
      }
    });

    // Sort by score (descending) and then by name (ascending)
    return results
      .sort((a, b) => {
        if (a.matchScore !== b.matchScore) {
          return b.matchScore - a.matchScore;
        }
        return a.tag.name.localeCompare(b.tag.name);
      })
      .slice(0, 10); // Limit to top 10 results
  }

  /**
   * Calculate match score between text and query
   */
  private calculateMatchScore(text: string, query: string): number {
    const normalizedText = text.toLowerCase();
    const normalizedQuery = query.toLowerCase();

    // Exact match
    if (normalizedText === normalizedQuery) {
      return 1.0;
    }

    // Starts with query
    if (normalizedText.startsWith(normalizedQuery)) {
      return 0.9;
    }

    // Contains query
    if (normalizedText.includes(normalizedQuery)) {
      return 0.7;
    }

    // Fuzzy match (simple character sequence)
    let queryIndex = 0;
    for (
      let i = 0;
      i < normalizedText.length && queryIndex < normalizedQuery.length;
      i++
    ) {
      if (normalizedText[i] === normalizedQuery[queryIndex]) {
        queryIndex++;
      }
    }

    if (queryIndex === normalizedQuery.length) {
      return 0.5; // All characters found in sequence
    }

    return 0; // No match
  }

  /**
   * Extract mentions from text using regex
   */
  public extractMentions(
    text: string,
  ): Array<{ mention: string; startIndex: number; endIndex: number }> {
    const mentions: Array<{
      mention: string;
      startIndex: number;
      endIndex: number;
    }> = [];
    const mentionRegex = /@([a-zA-Z0-9_]+(?:\s[a-zA-Z0-9_]+)*)/g;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const mention = match[1].trim(); // Remove leading/trailing spaces
      mentions.push({
        mention,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }

    return mentions;
  }

  /**
   * Resolve mentions to valid tags and return both valid and invalid mentions
   */
  public resolveMentionsToTags(
    text: string,
    availableTags: Tag[],
  ): {
    validTags: Array<{
      tag: Tag;
      mention: string;
      startIndex: number;
      endIndex: number;
    }>;
    invalidMentions: Array<{
      mention: string;
      startIndex: number;
      endIndex: number;
    }>;
  } {
    const mentions = this.extractMentions(text);
    const validTags: Array<{
      tag: Tag;
      mention: string;
      startIndex: number;
      endIndex: number;
    }> = [];
    const invalidMentions: Array<{
      mention: string;
      startIndex: number;
      endIndex: number;
    }> = [];

    // Create a map for fast tag lookup (case-insensitive)
    const tagMap = new Map<string, Tag>();
    availableTags.forEach((tag) => {
      tagMap.set(tag.name.toLowerCase(), tag);
    });

    mentions.forEach((mention) => {
      const tag = tagMap.get(mention.mention.toLowerCase());
      if (tag) {
        validTags.push({ tag, ...mention });
      } else {
        invalidMentions.push(mention);
      }
    });

    return { validTags, invalidMentions };
  }

  /**
   * Parse text containing both @mentions and <Tag:id>name</Tag> formats
   * Returns segments for UI rendering
   */
  public parseTextWithTags(
    text: string,
    availableTags: Tag[],
  ): ParsedSegment[] {
    const segments: ParsedSegment[] = [];
    let currentIndex = 0;

    // Check if input contains <Tag:id>name</Tag> format (from Tiptap)
    const hasTagFormat = /<Tag:([^>]+)>([^<]+)<\/Tag>/.test(text);

    if (hasTagFormat) {
      // Process Tiptap format: <Tag:id>name</Tag>
      const tagFormatRegex = /<Tag:([^>]+)>([^<]+)<\/Tag>/g;
      let match;

      while ((match = tagFormatRegex.exec(text)) !== null) {
        // Add text before this tag
        if (match.index > currentIndex) {
          segments.push({
            type: "text",
            content: text.slice(currentIndex, match.index),
          });
        }

        const tagId = match[1];
        const tagName = match[2];

        // Find the corresponding tag by ID or name
        const tag =
          availableTags.find((t) => t.id === tagId) ||
          availableTags.find(
            (t) => t.name.toLowerCase() === tagName.toLowerCase(),
          );

        if (tag) {
          segments.push({
            type: "valid-tag",
            content: match[0], // Keep original <Tag:id>name</Tag> content
            tag,
          });
        } else {
          segments.push({
            type: "invalid-tag",
            content: match[0], // Keep original <Tag:id>name</Tag> content
          });
        }

        currentIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (currentIndex < text.length) {
        segments.push({
          type: "text",
          content: text.slice(currentIndex),
        });
      }
    } else {
      // Process @mention format (for regular text input)

      // First pass: handle escaped @ symbols (\@)
      const escapedAtRegex = /\\@/g;
      const escapedPositions: number[] = [];
      let escapedMatch;

      while ((escapedMatch = escapedAtRegex.exec(text)) !== null) {
        escapedPositions.push(escapedMatch.index);
      }

      // Create a version without escaped @ for tag resolution
      const unescapedText = text.replace(/\\@/g, "@");

      // Get valid and invalid mentions from unescaped text
      const { validTags, invalidMentions } = this.resolveMentionsToTags(
        unescapedText,
        availableTags,
      );

      // Adjust positions back to original text (account for escaped @)
      const adjustPosition = (pos: number): number => {
        let adjustment = 0;
        for (const escapedPos of escapedPositions) {
          if (escapedPos < pos + adjustment) {
            adjustment += 1; // Each \@ adds 1 character
          }
        }
        return pos + adjustment;
      };

      // Combine all mentions and sort by position in original text
      const allMentions = [
        ...validTags.map((m) => ({
          ...m,
          type: "valid" as const,
          startIndex: adjustPosition(m.startIndex),
          endIndex: adjustPosition(m.endIndex),
        })),
        ...invalidMentions.map((m) => ({
          ...m,
          type: "invalid" as const,
          startIndex: adjustPosition(m.startIndex),
          endIndex: adjustPosition(m.endIndex),
        })),
      ].sort((a, b) => a.startIndex - b.startIndex);

      // Add escaped @ positions as segments
      const escapedSegments = escapedPositions.map((pos) => ({
        type: "escaped-at" as const,
        startIndex: pos,
        endIndex: pos + 2, // \@ is 2 characters
        content: text.slice(pos, pos + 2),
      }));

      // Combine and sort all segments
      const allSegments = [...allMentions, ...escapedSegments].sort(
        (a, b) => a.startIndex - b.startIndex,
      );

      // Process segments
      allSegments.forEach((segment) => {
        // Add text before this segment
        if (segment.startIndex > currentIndex) {
          segments.push({
            type: "text",
            content: text.slice(currentIndex, segment.startIndex),
          });
        }

        // Add the segment
        if (segment.type === "escaped-at") {
          segments.push({
            type: "escaped-at",
            content: "@", // Render as just @
          });
        } else if (segment.type === "valid") {
          segments.push({
            type: "valid-tag",
            content: text.slice(segment.startIndex, segment.endIndex),
            tag: segment.tag,
          });
        } else if (segment.type === "invalid") {
          segments.push({
            type: "invalid-tag",
            content: text.slice(segment.startIndex, segment.endIndex),
          });
        }

        currentIndex = segment.endIndex;
      });

      // Add remaining text
      if (currentIndex < text.length) {
        segments.push({
          type: "text",
          content: text.slice(currentIndex),
        });
      }
    }

    return segments;
  }

  /**
   * Extract tag name for display from various formats
   */
  public extractTagNameForDisplay(content: string): string {
    // Handle <Tag:id>name</Tag> format
    const tagFormatMatch = content.match(/<Tag:([^>]+)>([^<]+)<\/Tag>/);
    if (tagFormatMatch) {
      return `@${tagFormatMatch[2]}`;
    }

    // Handle @name format or add @ if missing
    if (!content.startsWith("@")) {
      return `@${content}`;
    }

    return content;
  }

  /**
   * Generate tag suggestions based on text content
   */
  public generateTagSuggestions(
    content: string,
    existingTags: Tag[],
  ): string[] {
    const suggestions = new Set<string>();

    // Extract potential tags from content
    const words = content.toLowerCase().match(/\b\w+\b/g) || [];

    // Filter words that could be good tags
    const potentialTags = words.filter((word) => {
      return word.length >= 3 && word.length <= 20;
    });

    // Add unique potential tags
    potentialTags.forEach((tag) => suggestions.add(tag));

    // Add similar existing tags
    existingTags.forEach((tag) => {
      if (content.toLowerCase().includes(tag.name.toLowerCase())) {
        suggestions.add(tag.name);
      }
    });

    return Array.from(suggestions).slice(0, 5);
  }

  /**
   * Format tag for display
   */
  public formatTagDisplay(tag: Tag): string {
    return `@${tag.name}`;
  }

  /**
   * Validate tag name
   */
  public validateTagName(name: string): { isValid: boolean; error?: string } {
    if (!name || !name.trim()) {
      return { isValid: false, error: "Tag name is required" };
    }

    const trimmedName = name.trim();

    if (trimmedName.length < 2) {
      return {
        isValid: false,
        error: "Tag name must be at least 2 characters",
      };
    }

    if (trimmedName.length > 50) {
      return {
        isValid: false,
        error: "Tag name must be less than 50 characters",
      };
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(trimmedName)) {
      return {
        isValid: false,
        error:
          "Tag name can only contain letters, numbers, hyphens, and underscores",
      };
    }

    return { isValid: true };
  }
}

// Export singleton instance
export const tagHandler = TagHandler.getInstance();
